import { LegalPageLayout } from "./LegalPageLayout";
import { LegalRouteLink } from "./LegalRouteLink";
import { getSupportEmail, getSupportMailtoHref } from "./supportEmail";
import { privacyUrl, termsUrl } from "@/lib/env";

const SUPPORT_EMAIL = getSupportEmail();
const SUPPORT_MAILTO = getSupportMailtoHref();

export function DeleteDataPage() {
  return (
    <LegalPageLayout title="Delete Data">
      <h1>How to Delete Your Screen0 Data</h1>
      <p className="legal-muted">Last updated: July 18, 2026</p>

      <h2>No account required</h2>
      <p>
        Screen0 does not require you to create an account or log in. Your data is associated with an
        anonymous device identifier generated on your device. You can delete that data using the options
        below.
      </p>

      <h2>Option 1 — Delete in the app (immediate)</h2>
      <p>
        You can delete your Screen0 data stored on our servers immediately from within the app. Follow
        these steps:
      </p>
      <ol>
        <li>Open Screen0.</li>
        <li>Go to <strong>Settings</strong>.</li>
        <li>Scroll to the <strong>Reset</strong> section.</li>
        <li>Tap <strong>Delete saved data and reset device ID</strong>.</li>
      </ol>
      <p>
        This action deletes data linked to your current anonymous device ID from Screen0 servers,
        including:
      </p>
      <ul>
        <li>Usage snapshots and screen time summaries</li>
        <li>Rules and Smart Block configuration</li>
        <li>Habits and related preferences</li>
        <li>Chat history from Ask Screen0</li>
        <li>Device preferences and trial reminders</li>
        <li>Your anonymous device identifier (a new one is created locally on your device afterward)</li>
      </ul>
      <p>
        Deletion is processed as soon as the action completes successfully. You do not need to contact
        support for this option.
      </p>

      <h2>Option 2 — Delete by email</h2>
      <p>
        If you cannot use the in-app option, you may email us and request deletion of data associated
        with your device. Include enough detail for us to locate your data (for example, when you used
        the app and any relevant device information you can share).
      </p>
      <p>
        Email:{" "}
        <a href={SUPPORT_MAILTO} className="legal-link">
          {SUPPORT_EMAIL}
        </a>
      </p>
      <p>
        We aim to respond to deletion requests within 30 days, subject to verification where needed and
        any legal obligations that require us to retain certain information.
      </p>

      <h2>What may be retained</h2>
      <p>
        Some information is handled outside Screen0 servers or is not removed by the in-app reset:
      </p>
      <ul>
        <li>
          <strong>Purchase and subscription records</strong> — transaction and entitlement verification
          handled by Google Play, Apple App Store, and subscription infrastructure such as RevenueCat.
          Manage subscriptions and purchase history in your store account settings.
        </li>
        <li>
          <strong>Onboarding analytics</strong> — anonymized onboarding events may remain stored
          separately and are not removed by the in-app reset described above.
        </li>
        <li>
          <strong>Automatic retention</strong> — usage snapshots may also be deleted automatically after
          a limited retention period (typically up to 90 days), as described in our Privacy Policy.
        </li>
      </ul>

      <h2>Local device permissions</h2>
      <p>
        The in-app reset deletes data stored on Screen0 servers. It does not revoke system permissions on
        your device. On Android, to stop Screen0 from accessing usage statistics, revoke{" "}
        <strong>Usage Access</strong> in your device&apos;s system settings (Settings → Apps → Screen0 →
        Usage access, or your device manufacturer&apos;s equivalent path).
      </p>

      <h2>Related policies</h2>
      <p>
        For more about what we collect and how long we keep it, see our{" "}
        <LegalRouteLink href={privacyUrl} className="legal-link">
          Privacy Policy
        </LegalRouteLink>{" "}
        and{" "}
        <LegalRouteLink href={termsUrl} className="legal-link">
          Terms of Use
        </LegalRouteLink>
        .
      </p>
    </LegalPageLayout>
  );
}
